// shared-lib/kitchen-assembly/styling/defaultKitchenBacksplash.js
//
// The backsplash a kitchen laid out anew gets (user, 2026-10-20, item #42):
// the bands the builder already knows how to draw — over the counter, up to
// the uppers or 18" where none hang, up to the hood behind the range, cut
// around windows — in the default tile, each marked not chosen
// (`unchosenBacksplash.js`). Run by both layout applies, the kitchen's
// (`applyCabinetLayout`) and the great room's, on the room they are about to
// write. Pure.

const {
  buildBacksplashRegions,
  findBacksplashRegions,
  isGeneratedKitchenBacksplash,
  joinRoomOpenings,
  rebuildGeneratedBacksplash,
  replaceGeneratedBacksplash,
  unhydratedOpenings,
} = require('./kitchenBacksplash');
const {
  DEFAULT_BACKSPLASH_CONFIG,
  DEFAULT_BACKSPLASH_TILE_ID,
} = require('./unchosenBacksplash');

/** The provenance a layout apply's bands carry, as its lights' `addedBy` does. */
const LAYOUT_PROVENANCE_TOOL = 'kitchen-layout';

/**
 * The room's walls with its default backsplash drawn, or why not.
 *
 * - Bands the generator drew before are RE-FITTED to the new layout
 *   (`rebuildGeneratedBacksplash`): a tile someone picked stays picked, the
 *   default stays not chosen.
 * - A backsplash somebody drew by hand is theirs: nothing is drawn over it.
 * - A window or cased opening that cannot be measured stops the draw — a band
 *   run across glass is a worse error than no band (the rule
 *   `apply_backsplash_texture` and the rebuild already follow).
 *
 * @param {Object} room  the room as it will be written: `walls`, the layout's
 *   `objects`, its `windows` / `openings` (slim copies are joined to
 *   `structureOpenings`)
 * @param {Object} [opts]
 * @param {{windows?: Array, openings?: Array}} [opts.structureOpenings]
 * @param {string} [opts.generatedAt]
 * @returns {{walls: Array|null, reason: string|null}} `reason` is null when
 *   walls are returned, else 'hand-drawn-backsplash' | 'unhydrated-openings'
 *   | 'rebuild-declined' | 'no-counter'
 */
function planDefaultBacksplash(room, opts = {}) {
  const walls = room?.walls ?? [];
  const generatedAt = opts.generatedAt ?? new Date().toISOString();
  const existing = findBacksplashRegions(walls);
  if (existing.some(({ region }) => !isGeneratedKitchenBacksplash(region))) {
    return { walls: null, reason: 'hand-drawn-backsplash' };
  }
  if (existing.length) {
    const rebuilt = rebuildGeneratedBacksplash(room, {
      structureOpenings: opts.structureOpenings,
      generatedAt,
    });
    return rebuilt
      ? { walls: rebuilt, reason: null }
      : { walls: null, reason: 'rebuild-declined' };
  }

  const joined = joinRoomOpenings(room, opts.structureOpenings);
  if (unhydratedOpenings(joined).length) {
    return { walls: null, reason: 'unhydrated-openings' };
  }
  const regions = buildBacksplashRegions(
    { ...joined, objects: joined.objects || [] },
    DEFAULT_BACKSPLASH_CONFIG,
    {
      counterBacksplash: DEFAULT_BACKSPLASH_TILE_ID,
      stoveBacksplash: DEFAULT_BACKSPLASH_TILE_ID,
    },
    { generatedAt }
  ).map((r) => ({
    ...r,
    unchosen: true,
    styleProvenance: { ...r.styleProvenance, tool: LAYOUT_PROVENANCE_TOOL },
  }));
  if (!regions.length) return { walls: null, reason: 'no-counter' };
  return { walls: replaceGeneratedBacksplash(walls, regions), reason: null };
}

module.exports = { planDefaultBacksplash, LAYOUT_PROVENANCE_TOOL };
